import { Button, Checkbox, Input, Modal, Space, Typography } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { SegmentationLabel } from 'models/segmentation';
import {
  ScrollableList,
  SelectButtonsRow,
  InfoTextRow,
  CheckboxRow,
  CommentBox,
} from './RejectModal.styled';

const { Text } = Typography;

interface IProps {
  isModalOpen: boolean;
  handleCancel: () => void;
  handleOk: (acceptedSegmentations: string[], comment: string) => void;
  segmentations: SegmentationLabel[];
  initialAcceptedIds?: string[];
}

const AcceptModal: FC<IProps> = props => {
  const { isModalOpen, handleCancel, handleOk, segmentations, initialAcceptedIds = [] } = props;
  const [comment, setComment] = useState('');
  const [acceptedSegmentations, setAcceptedSegmentations] = useState<Set<string>>(
    new Set(initialAcceptedIds)
  );

  useEffect(() => {
    if (isModalOpen) {
      // default to selecting all items for faster technician reviews
      const allIds = segmentations.map(seg => seg.icid);
      setAcceptedSegmentations(new Set(allIds));
      setComment('');
    }
  }, [isModalOpen, initialAcceptedIds]);

  const handleCheckboxChange = (segmentationId: string, checked: boolean) => {
    setAcceptedSegmentations(prev => {
      const newSet = new Set(prev);
      if (checked) newSet.add(segmentationId);
      else newSet.delete(segmentationId);
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = segmentations.map(seg => seg.icid);
    setAcceptedSegmentations(new Set(allIds));
  };

  const handleSelectNone = () => {
    setAcceptedSegmentations(new Set());
  };

  return (
    <Modal
      title="Select Segmentations to Accept"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <SelectButtonsRow>
            <Button size="small" onClick={handleSelectAll} style={{ marginRight: 8 }}>
              Select All
            </Button>
            <Button size="small" onClick={handleSelectNone}>
              Select None
            </Button>
          </SelectButtonsRow>
          <InfoTextRow>
            <Text type="secondary">
              Only selected segmentations will be accepted; others will remain unchanged.
            </Text>
          </InfoTextRow>
        </div>

        <ScrollableList>
          {segmentations.map(segmentation => {
            const isAccepted = acceptedSegmentations.has(segmentation.icid);
            return (
              <CheckboxRow key={segmentation.icid}>
                <Checkbox
                  checked={isAccepted}
                  onChange={e => handleCheckboxChange(segmentation.icid, e.target.checked)}
                >
                  <Space direction="vertical" size={0}>
                    <Text strong>{segmentation.segmentationClassValue}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {segmentation.icid}
                      {isAccepted && ' (Currently Accepted)'}
                    </Text>
                  </Space>
                </Checkbox>
              </CheckboxRow>
            );
          })}
        </ScrollableList>

        <CommentBox>
          <Text strong>Comment (optional):</Text>
          <Input.TextArea
            showCount
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={255}
            placeholder="Add a comment for the accepted segmentations..."
            style={{ marginTop: 8, resize: 'none' }}
          />
        </CommentBox>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} size={12}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => handleOk(Array.from(acceptedSegmentations), comment)}
            disabled={acceptedSegmentations.size === 0}
          >
            Accept ({acceptedSegmentations.size} selected)
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

export default AcceptModal;
